import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatMenuTrigger } from '@angular/material/menu';
import * as moment from 'moment';
import { SellOrdersFilterParams, SellOrdersSortParams } from 'src/app/core/services/sell-orders.service';
import { DEFAULT_DATE_FORMAT } from 'src/app/helpers/date-format.helper';

@Component({
  selector: 'app-sell-orders-table-header',
  templateUrl: './sell-orders-table-header.component.html',
  styleUrls: ['./sell-orders-table-header.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: DEFAULT_DATE_FORMAT }]
})
export class SellOrdersTableHeaderComponent implements OnInit {
  @Output() filterValues = new EventEmitter();
  minDate: Date;
  maxDate = new Date();
  outOrderEventHistoryDispatchedAtMinDate = new Date();
  outOrderEventHistoryDispatchedAtMaxDate = new Date();
  showCheckIcon: { [key: string]: boolean } = {};
  appliedFilters = {};
  sortDirection = '';
  sortParams: SellOrdersSortParams;
  filterParams: SellOrdersFilterParams;
  sortColumn = '';
  form = new FormGroup({
    startDate: new FormControl(null),
    endDate: new FormControl(null),
    totalQuantity: new FormControl(null),
    outOrderEventHistoryDispatchedAtStartDate: new FormControl(null),
    outOrderEventHistoryDispatchedAtEndDate: new FormControl(null),
    productName: new FormControl(null),
    no: new FormControl(null),
  });

  constructor() { }

  get isdateSaveBtnDisabled(): boolean {
    const start = this.form.get('startDate');
    const end = this.form.get('endDate');
    const bothDatesValid = start?.valid && end?.valid;
    const bothDatesEmpty = !start?.value && !end?.value;
  
    if (this.showCheckIcon['date']) {
      return !(bothDatesValid || bothDatesEmpty);
    } else {
      return !bothDatesValid;
    }
  }

  get isoutOrderEventHistoryDispatchedAtSaveBtnDisabled(): boolean {
    const start = this.form.get('outOrderEventHistoryDispatchedAtStartDate');
    const end = this.form.get('outOrderEventHistoryDispatchedAtEndDate');
    const bothDatesValid = start?.valid && end?.valid;
    const bothDatesEmpty = !start?.value && !end?.value;
  
    if (this.showCheckIcon['outOrderEventHistoryDispatchedAt']) {
      return !(bothDatesValid || bothDatesEmpty);
    } else {
      return !bothDatesValid;
    }
  }

  ngOnInit(): void {
  }

  applyFilters(menu: MatMenuTrigger) {
    const startDate = this.form.value.startDate ? moment(this.form.value.startDate._d).valueOf() : null;
    const endDate = this.form.value.endDate ? moment(this.form.value.endDate._d).endOf('day').valueOf() : null;
    const outOrderEventHistoryDispatchedAtStartDate = this.form.value.outOrderEventHistoryDispatchedAtStartDate ? moment(this.form.value.outOrderEventHistoryDispatchedAtStartDate._d).valueOf() : null;
    const outOrderEventHistoryDispatchedAtEndDate = this.form.value.outOrderEventHistoryDispatchedAtEndDate ? moment(this.form.value.outOrderEventHistoryDispatchedAtEndDate._d).endOf('day').valueOf() : null;

    this.filterParams = {
      dateStartDate: startDate,
      dateEndDate: endDate,
      outOrderEventHistoryDispatchedAtStartDate: outOrderEventHistoryDispatchedAtStartDate,
      outOrderEventHistoryDispatchedAtEndDate: outOrderEventHistoryDispatchedAtEndDate,
      totalQuantity: this.form.value.totalQuantity,
      productName: this.form.value.productName,
      no: this.form.value.no,
    };

    this.removeNullKeys(this.filterParams);
    this.sortColumn = this.sortParams?.orderBy;
    this.filterValues.emit({
      filterParams: this.filterParams,
      sortParams: this.sortParams
    });
    menu.closeMenu();
  }

  sort(column: SellOrdersSortParams['orderBy'], direction: SellOrdersSortParams['sortBy']) {
    this.sortParams = null;
    this.showCheckIcon = {};
    this.sortDirection = direction;
    this.showCheckIcon[column] = true;
    this.sortParams = {
      orderBy: column,
      sortBy: direction
    };
  }

  clearFilters(menu: MatMenuTrigger, filter: string) {
    this.maxDate = new Date();
    this.minDate = null;
    this.outOrderEventHistoryDispatchedAtMinDate = null;
    this.outOrderEventHistoryDispatchedAtMaxDate = new Date();
    this.sortColumn = this.sortColumn === filter ? null : this.sortColumn;
    this.sortParams = this.sortParams?.orderBy === filter ? null : this.sortParams;
    delete this.appliedFilters?.[filter];
    delete this.showCheckIcon?.[filter];

    switch (filter) {
      case 'no':
        this.form.get('no').reset();
        delete this.filterParams?.['no'];
        break;
      case 'totalQuantity':
        this.form.get('totalQuantity').reset();
        delete this.filterParams?.['totalQuantity'];
        break;
      case 'date':
        this.form.get('startDate').reset();
        this.form.get('endDate').reset();
        delete this.filterParams?.['dateStartDate'];
        delete this.filterParams?.['dateEndDate'];
        break;
      case 'outOrderEventHistoryDispatchedAt':
        this.form.get('outOrderEventHistoryDispatchedAtStartDate').reset();
        this.form.get('outOrderEventHistoryDispatchedAtEndDate').reset();
        delete this.filterParams?.['outOrderEventHistoryDispatchedAtStartDate'];
        delete this.filterParams?.['outOrderEventHistoryDispatchedAtEndDate'];
        break;
      case 'productName':
        this.form.get('productName').reset();
        delete this.filterParams?.['productName'];
        break;
    }

    this.filterValues.emit({
      filterParams: this.filterParams,
      sortParams: this.sortParams
    });
    menu.closeMenu();
  }

  removeNullKeys(params) {
    Object.keys(params).forEach(key => {
      if (params[key] === null) {
        delete params[key];
      } else {
        this.setAppliedFilters(key);
      }
    });
  }

  setAppliedFilters(name: string) {
    switch (name) {
      case 'dateStartDate':
      case 'dateEndDate':
        this.appliedFilters['date'] = true;
        break;
      case 'outOrderEventHistoryDispatchedAtStartDate':
      case 'outOrderEventHistoryDispatchedAtEndDate':
        this.appliedFilters['outOrderEventHistoryDispatchedAt'] = true;
        break;
      default:
        this.appliedFilters[name] = true;
        break;
    }
  }

  ondateStartDateChange() {
    this.minDate = this.form.value.startDate._d;
  }

  ondateEndDateChange() {
    this.maxDate = this.form.value.endDate._d;
  }

  onOutOrderDispatchedAtStartDateChange() {
    this.outOrderEventHistoryDispatchedAtMinDate = this.form.value.outOrderEventHistoryDispatchedAtStartDate._d;
  }

  onOutOrderDispatchedAtEndDateChange() {
    this.outOrderEventHistoryDispatchedAtMaxDate = this.form.value.outOrderEventHistoryDispatchedAtEndDate._d;
  }

}
